import translate from "@insite/client-framework/Translate";
import { ProductInventoryDto, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import {
    DataTable,
    DataTableBody,
    DataTableHead,
    DataTableHeader,
    Link,
    Typography,
} from "@insite/mobius/index";
import Modal from "@insite/mobius/Modal";
import * as React from "react";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { AUTH_SSO_SIGN_IN } from "@thkStore/Common/Constants/AuthConstants";
import { CustomIncludeModalConfig } from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { ApplicationMemberAppConfigState } from "@thkStore/Store/Components/MemberAppConfig/MemberAppConfigReducer";
import { ItemWithHelpInfoHelp } from "@thkStore/Components/Tooltip/ItemWithHelpInfo";
import { HAS_INCLUDES_PRODUCTS } from "@thkStore/Constants/ProductPropertiesKeys";
import { DocumentNameValues, DocumentTypeValues } from "@thkStore/Types/DocumentTypeValues";
import { CustomVariantTraitModel } from "@thkStore/Types/VariantChildrenGridModel";
import { hideTrait } from "@thkStore/Widgets/VariantChildrenGrid/utils";
import VariantChildrenGridContext from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridContext";
import VariantChildrenGridVariantTableRow from "@thkStore/Widgets/VariantChildrenGrid/VariantChildrenGridVariantTableRow";
import { connect } from "react-redux";
import styled from "styled-components";

interface OwnProps {
    variants: ProductModel[];
    variantTraitsWithCustomProperties: CustomVariantTraitModel[];
    pricingByProductId: Record<string, ProductPriceDto>;
    inventoryByProductId: Record<string, ProductInventoryDto>;
    includesText: string;
    parentProductId: string;
    customRailModalConfig: CustomIncludeModalConfig;
}

const mapStateToProps = (state: ApplicationState) => {
    const memberAppConfig = (state as ApplicationMemberAppConfigState).memberAppConfig;
    return {
        isAuthenticated: state.context.session.isAuthenticated,
        settingsCollection: getSettingsCollection(state),
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        thkMemberAppUrl: memberAppConfig?.thkMemberAppUrl || "",
        enableThkMemberAppRedirection: memberAppConfig?.enableThkMemberAppRedirection || false,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const TableContainer = styled.div`
    max-height: 724px;
    overflow-x: auto;
    overflow-y: auto;

    @media (max-width: 767px) {
        overflow-x: hidden;
    }

    table {
    
        thead {
            position: sticky;
            top: 0;
            z-index: 3;
        
            tr {
            
                th {
                    font-size: 16px;
                    color: #071D49;
                    background-color: ${({ theme }) => theme.colors.secondary.main};
                    border-bottom: 1px solid #C7C9C7;

                    span {
                        font-size: 16px;
                        color: #071D49;
                        display: flex;
                        align-items: center;
                        height: 100%;
                        padding-top: 4px;
                    }
                }
            }
        }

        .frozen-items {
            position: sticky;
            left: 0;
            z-index: 2;

            @media (max-width:768px){
                height: auto;
                padding-right: 0;

                &:before {
                    display: none !important;
                }
            }

            &__container {
                display: flex;
                align-items: center;
                padding-top: 4px;

                @media (max-width:768px){
                    flex-direction: column;
                    align-items: flex-end;
                    width: 100%;
                    padding-top: 0;
                }
            
                & > div {
                    padding: 0 12px;

                    @media (max-width:768px){
                        height: 65px;
                        align-items: center;
                        display: grid;
                        grid-template-columns: 49% 51%;
                        width: 100% !important;
                        padding: 0;

                        &:not([data-has-label="true"]) {

                            &:before {
                                content: attr(data-label);
                                background-color: ${({ theme }) => theme.colors.secondary.main};
                                height: 65px;
                                align-items: center;
                                display: flex;
                                padding: 8px;
                                font-weight: bold;
                                color: #071D49;
                                border-bottom: 1px solid #6b6b6b;
                            }
                        }

                        a, & > span {
                            justify-content: flex-end;
                            padding: 0 8px;
                            border-bottom: 1px solid #6b6b6b;
                            display: flex !important;
                            align-items: center;
                            height: 65px;
                        }

                        &:last-child {

                            &:not([data-has-label="true"]):before, a, & > span {
                                border-bottom: none;
                            }
                        }
                    }

                    &[data-label="Product Number"] {
                        width: 300px;

                        @media (max-width:768px){

                            & > a {
                                white-space: normal;
                            }
                        }
                    }

                    &[data-label="CAD"] {
                        width: 60px;
                        text-align: center;
                    }

                    &[data-label="Catalog"] {
                        width: 83px;
                        text-align: center;
                    }

                    &[data-label="Quantity"] {
                        width: 124px;
                    }

                    &[data-label="Includes"] {
                        width: 124px;
                        text-align: center;
                        span {
                            text-transform: capitalize;
                        }                        
                    }

                    &[data-label="Days to Ship"] {
                        width: 121px;
                    }

                    &[data-label="Price"] {
                        width: 110px;
                    }

                    &[data-label="Add to Cart"] {
                        width: 173px;
                    }
                }
            }
        }

        tbody {
        
            tr {
            
                td {
                    height: 65px;

                    &.frozen-items {
                        background-color: white;
                    }

                    &[data-label="Includes"] {
                        text-align: center;
                    }
                }
            }
        }

        @media (max-width: 767px) {
            thead {
                display: none;
            }
        }
    }
`;

const VariantDataTable: React.FC<Props> = ({
    variants,
    variantTraitsWithCustomProperties,
    isAuthenticated,
    pricingByProductId,
    inventoryByProductId,
    includesText,
    settingsCollection,
    signInPageUrl,
    thkMemberAppUrl,
    enableThkMemberAppRedirection,
    parentProductId,
    customRailModalConfig,
}) => {
    const [loginModalOpen, setLoginModalOpen] = React.useState(false);

    const openLoginModal = React.useCallback(() => {
        setLoginModalOpen(true);
    }, []);

    const showCADColumn = React.useMemo(
        () =>
            variants.some(variant =>
                variant.documents?.some(
                    doc =>
                        doc.documentType.toLocaleLowerCase() === DocumentTypeValues.Downloads.toLowerCase() &&
                        doc.name.toLocaleLowerCase() === DocumentNameValues.CAD.toLowerCase(),
                ),
            ),
        [variants],
    );
    const showCategoryDocumentColumn = React.useMemo(
        () =>
            variants.some(variant =>
                variant.documents?.some(
                    doc => doc.documentType.toLocaleLowerCase() === DocumentTypeValues.Catalog.toLowerCase(),
                ),
            ),
        [variants],
    );

    const showIncludesColumn = React.useMemo(
        () => variants.some(v => v.properties[HAS_INCLUDES_PRODUCTS] === "true"),
        [variants],
    );

    const handleSignInClick = () => {
        const currentPageUrl = window.location.pathname + window.location.search;
        let redirectUrl: string;
        if (enableThkMemberAppRedirection && thkMemberAppUrl) {
            const autoSsoUrl = `${window.location.origin}${AUTH_SSO_SIGN_IN}`;
            redirectUrl = `${thkMemberAppUrl}?red=${autoSsoUrl}?returnUrl=${currentPageUrl}`;
        } else {
            const loginSettingSettings = (settingsCollection as any)["loginSettingSettings"] || "";
            const loginRedirectUrl = loginSettingSettings?.loginRedirectUrl || "";
            const baseUrl = loginRedirectUrl || signInPageUrl || AUTH_SSO_SIGN_IN;
            redirectUrl = `${baseUrl}?returnUrl=${encodeURIComponent(currentPageUrl)}`;
        }
        window.location.href = redirectUrl;
    };

    return (
        <TableContainer>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader className="frozen-items">

                        <StyledWrapper className="frozen-items__container">

                            <StyledWrapper data-label="Product Number">{translate("Product Title")}</StyledWrapper>
                            {(showCADColumn) && (
                                <StyledWrapper data-label="CAD">{translate("CAD")}</StyledWrapper>
                            )}
                            {(showCategoryDocumentColumn) && (
                                <StyledWrapper data-label="Catalog">{translate("Catalog")}</StyledWrapper>
                            )}

                        </StyledWrapper>

                    </DataTableHeader>
                    {isAuthenticated && (
                        <DataTableHeader data-label="Quantity">{translate("Quantity")}</DataTableHeader>
                    )}
                    {(showIncludesColumn && isAuthenticated) && (
                        <DataTableHeader data-label="Includes">{translate(`Add ${includesText}`)}</DataTableHeader>
                    )}
                    { isAuthenticated && (
                        <DataTableHeader data-label="Days to Ship">{translate("Days to Ship")}</DataTableHeader>
                    )}
                    { isAuthenticated && (
                        <DataTableHeader data-label="Price">{translate("Price")}</DataTableHeader>
                    )}
                    {variantTraitsWithCustomProperties.map(trait => (
                        !hideTrait(trait.customProperties) &&
                        <DataTableHeader key={trait.id}>
                            <ItemWithHelpInfoHelp
                                title={trait.nameDisplay || trait.name}
                                helpContent={trait.customProperties}
                            />
                        </DataTableHeader>
                    ))}                    
                </DataTableHead>
                <DataTableBody>
                    {variants.map((variant, idx) => {
                        return (
                            <VariantChildrenGridContext product={variant} idx={idx} key={variant.id} pricing={pricingByProductId[variant.id]} inventory={inventoryByProductId[variant.id]} parentProductId={parentProductId}>
                                <VariantChildrenGridVariantTableRow
                                    key={variant.id}
                                    variantTraits={variantTraitsWithCustomProperties}
                                    showCADColumn={showCADColumn}
                                    showCategoryDocumentColumn={showCategoryDocumentColumn}
                                    isAuthenticated={isAuthenticated}
                                    showIncludesColumn={showIncludesColumn}
                                    onLoginRequired={openLoginModal}
                                    customRailModalConfig={customRailModalConfig}
                                />
                            </VariantChildrenGridContext>
                        );
                    })}
                </DataTableBody>
            </DataTable>
            <Modal
                headline={translate("Sign In Required")}
                isOpen={loginModalOpen}
                handleClose={() => setLoginModalOpen(false)}
                sizeVariant="small"
            >                
                <Typography>{translate("Please sign in to download CAD files.")}</Typography>
                <br />
                <Link onClick={handleSignInClick}>
                    <Typography>{translate("Sign In")}</Typography>
                </Link>
            </Modal>
        </TableContainer>
    );
};

export default connect(mapStateToProps)((VariantDataTable));